import React, { useState, useEffect, useContext, useRef } from "react";
import "./LoadProfile.css";
import PropTypes from "prop-types";
import { Outlet, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress } from "@mui/material";
import PrimaryButton from "../Buttons/PrimaryButton/PrimaryButton";
import PrimaryTabs from "../TabPanels/PrimaryTabPanel/PrimaryTabs/PrimaryTabs";
import PrimaryTabPanel from "../TabPanels/PrimaryTabPanel/PrimaryTabPanel";
import LoadProfileHeader from "./LoadProfileHeader/LoadProfileHeader";
import LoadProfileDetailsContainer from "./LoadProfileDetailsContainer/LoadProfileDetailsContainer";
import LoadFreightMatches from "./LoadFreightMatches/LoadFreightMatches";
import LoadProfileCallins from "./LoadProfileCallins/LoadProfileCallins";
import LoadProfileLaneHistory from "./LoadProfileLaneHistory/LoadProfileLaneHistory";
import LoadProfileDocuments from "./LoadProfileDocuments/LoadProfileDocuments";
import LoadProfileOffers from "./LoadProfileOffers/LoadProfileOffers";
import ShotClock from "../ShotClock/ShotClock";
import useFreightMatchReceiver from "../../hooks/useFreightMatchReceiver/useFreightMatchReceiver";
import { handleAltEnter } from "../../utils/keyboardShortcutHelpers";
import { sortArrayOfDateObjectsDesc, sortArrayOfNumbersAsc } from "../../utils/sortHelpers";
import { LoadService } from "../../services/LoadService";
import { CarrierService } from "../../services/CarrierService";
import { MovementService } from "../../services/MovementService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";
import { MAXPAY_UPDATED_EVENT } from "../../constants/syncConstants";

export default function LoadProfile({
  newcomer,
  refreshPage,
  setRefreshPage,
  onLoadFieldUpdate,
  handleTableAction,
  handleRecentlyViewed
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  let [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [loadId, setLoadId] = useState("");
  const [activeLoad, setActiveLoad] = useState({});
  const activeLoadRef = useRef(activeLoad);
  activeLoadRef.current = activeLoad;
  const [isLoading, setIsLoading] = useState(true);
  const [carrierExtraPay, setCarrierExtraPay] = useState([]);
  const [carrierExtraPayLoading, setCarrierExtraPayLoading] = useState(false);
  const [maxPayInfo, setMaxPayInfo] = useState({});
  const [callins, setCallins] = useState([]);
  const [stopsUpdated, setStopsUpdated] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [clearTimeFromStopLoading, setClearTimeFromStopLoading] = useState(false);
  const [availableToBook, setAvailableToBook] = useState(false);
  const [freightMatches, setFreightMatches] = useState([]);
  const [newLoadsCount, setNewLoadsCount] = useState(0);
  const [radiusOrigin, setRadiusOrigin] = useState("50");
  const [radiusDest, setRadiusDest] = useState("50");
  const {
    newLoadMatches,
    setNewLoadMatches
  } = useFreightMatchReceiver("load");

  const radiusOptions = [
    {
      name: "25 mi",
      value: "25"
    },
    {
      name: "50 mi",
      value: "50"
    },
    {
      name: "100 mi",
      value: "100"
    },
    {
      name: "150 mi",
      value: "150"
    },
    {
      name: "200 mi",
      value: "200"
    },
    {
      name: "250 mi",
      value: "250"
    },
    {
      name: "300 mi",
      value: "300"
    },
    {
      name: "350 mi",
      value: "350"
    },
    {
      name: "400 mi",
      value: "400"
    },
    {
      name: "450 mi",
      value: "450"
    },
    {
      name: "500 mi",
      value: "500"
    }
  ];

  const tabLabels = [
    {
      name: "Details"
    },
    {
      name: "Matches"
    },
    {
      name: "Tracking"
    },
    {
      name: "History"
    },
    {
      name: "Documents"
    },
    {
      name: "Offers"
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    searchParams.set("tab", newValue);
    window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
  };

  const handleUpdateStops = () => {
    if (stopsUpdated.length) {
      setSaveLoading(true);
      stopsUpdated.forEach(stop => {
        CarrierService.UpdateStop(instance, stop, activeLoad.payeeId).then(() => {
          setStopsUpdated([]);
          setSaveLoading(false);
        }).catch(error => {
          console.error(error);
          setSaveLoading(false);
          warning(error);
        });
      });
    }
  };

  const clearTimeFromStops = (stop) => {
    setClearTimeFromStopLoading(true);
    let stops = activeLoad.stops.filter(st => st.orderSequence > stop.orderSequence && st.actualArrival != null);
    stops.forEach(st => {
      st.actualArrival = null;
      st.actualDeparture = null;
    });
    stops.push(stop);
    stops = stops.concat(activeLoad.stops.filter(st => st.orderSequence < stop.orderSequence));
    let lastStopOrderSequence = sortArrayOfNumbersAsc(activeLoad.stops, "orderSequence").slice(-1)[0].orderSequence;
    CarrierService.ClearTimeStop(instance, stops, activeLoad.payeeId, lastStopOrderSequence).then(() => {
      setStopsUpdated([]);
      setClearTimeFromStopLoading(false);
    }).catch(error => {
      console.error(error);
      setClearTimeFromStopLoading(false);
      warning(error);
    });
  };

  const handleGetMatches = (manualRefresh = false) => {
    if (activeLoad.orderId || activeLoad.id) {
      const searchParams = {
        orderId: activeLoad.orderId ?? activeLoad.id,
        originSearchMiles: radiusOrigin,
        destinationSearchMiles: radiusDest
      };

      setIsLoading(true);
      CarrierService.GetLoadFreightMatches(instance, searchParams).then(res => {
        let filteredLoads = res.loads.filter(({ removedFlag }) => !removedFlag);
        filteredLoads.forEach(load => { load.newlyAdded = false; });
        setNewLoadsCount(0);

        if (newLoadMatches && !manualRefresh) {
          let oldLoads = filteredLoads.filter(load => freightMatches.some(({ orderId, carrierId }) => orderId === load.orderId && carrierId === load.carrierId));
          let newLoads = filteredLoads.filter(load => !freightMatches.some(({ orderId, carrierId }) => orderId === load.orderId && carrierId === load.carrierId));
          newLoads.forEach(load => { load.newlyAdded = true; });
          setNewLoadsCount(newLoads.length);

          filteredLoads = newLoads.concat(oldLoads);
        }

        setFreightMatches(filteredLoads);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  const getCarrierExtraPay = () => {
    setCarrierExtraPayLoading(true);
    CarrierService.GetCarrierExtraPay(instance, loadId).then(res => {
      setCarrierExtraPay(res);
      setCarrierExtraPayLoading(false);
    }).catch(error => {
      console.error(error);
      setCarrierExtraPayLoading(false);
      warning(error);
    });
  };

  const getMaxPay = () => {
    MovementService.GetMaxPay(instance, loadId).then(res => {
      setMaxPayInfo(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  const getLoadCallins = () => {
    CarrierService.GetCallInsByOrder(instance, loadId).then(res => {
      setCallins(sortArrayOfDateObjectsDesc(res.callins, "callDateTime"));
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  const getLoad = (initialLoad) => {
    if (loadId?.length) {
      if (initialLoad) {
        setIsLoading(true);
      }

      LoadService.GetLoad(instance, loadId).then(res => {
        const originStop = res.stops[0];
        const destinationStop = res.stops[res.stops.length - 1];
        const item = {
          type: "load",
          loadId: loadId,
          status: res.brokerageStatus,
          lane: `${originStop.cityName} > ${destinationStop.cityName}`,
          isBookmark: false
        };
        handleRecentlyViewed(item);

        setActiveLoad(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });

      getCarrierExtraPay();
      getMaxPay();
      getLoadCallins();
    }
  };

  useEffect(() => {
    const newLoad = newcomer?.load;
    if (newLoad?.orderId === loadId) {
      const current = activeLoadRef.current;
      const isMaxPayOnlyUpdate = newcomer?.maxPayOnly || (
        typeof newLoad?.maxPay === "number" &&
        current?.id === loadId &&
        newLoad?.brokerageStatus === current?.brokerageStatus &&
        newLoad?.maxPay !== current?.maxPay
      );

      if (isMaxPayOnlyUpdate) {
        setActiveLoad(old => {
          if (!old?.id || old.id !== loadId) {
            return old;
          }
          if (Number(old.maxPay) === Number(newLoad.maxPay)) {
            return old;
          }

          return {
            ...old,
            maxPay: Number(newLoad.maxPay),
          };
        });
        return;
      }

      setRefreshPage(true);
    }
  }, [newcomer, loadId]);

  useEffect(() => {
    if (refreshPage) {
      getLoad();
      setRefreshPage(false);
    }
  }, [refreshPage]);

  useEffect(() => {
    if (loadId) {
      getLoad(true);
    }
  }, [loadId]);

  useEffect(() => {
    setLoadId(searchParams.get("loadId"));

    const tab = searchParams.get("tab");
    setTabValue(tab ? parseInt(tab) : 0);
  }, [searchParams]);

  const activeLoadIdRef = useRef(activeLoad?.id);
  const prevActiveLoadRef = useRef(activeLoad);

  useEffect(() => {
    // Skip matches re-fetch if only maxPay changed on the same load
    const loadIdentityChanged = activeLoad?.id !== activeLoadIdRef.current;
    const prev = prevActiveLoadRef.current;
    const isMaxPayOnlyChange = !loadIdentityChanged && prev?.id === activeLoad?.id &&
      prev?.maxPay !== activeLoad?.maxPay &&
      prev?.brokerageStatus === activeLoad?.brokerageStatus;

    activeLoadIdRef.current = activeLoad?.id;
    prevActiveLoadRef.current = activeLoad;

    if (isMaxPayOnlyChange) {
      return;
    }

    handleGetMatches();
  }, [activeLoad, radiusOrigin, radiusDest]);

  useEffect(() => {
    if (newLoadMatches) {
      handleGetMatches();
      setNewLoadMatches(false);
    }
  }, [newLoadMatches]);

  useEffect(() => {
    const handleMaxPayUpdated = event => {
      const { orderId, maxPay: nextMaxPay } = event?.detail ?? {};
      if (!orderId || orderId !== loadId) {
        return;
      }

      setActiveLoad(old => {
        if (!old?.id || old.id !== orderId) {
          return old;
        }
        // Skip update if value is already the same
        if (Number(old.maxPay) === Number(nextMaxPay)) {
          return old;
        }
        return {
          ...old,
          maxPay: Number(nextMaxPay),
        };
      });
      getMaxPay();
    };

    window.addEventListener(MAXPAY_UPDATED_EVENT, handleMaxPayUpdated);
    return () => window.removeEventListener(MAXPAY_UPDATED_EVENT, handleMaxPayUpdated);
  }, [loadId]);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, stopsUpdated.length === 0, handleUpdateStops)} sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={50} />
        </Box>
      ) : (
        <Box sx={{ backgroundColor: "white_opacity.100" }}>
          <Box
            sx={{
              position: "sticky",
              zIndex: 999,
              backgroundColor: "white_opacity.100",
              width: "100%"
            }}
          >
            <LoadProfileHeader
              activeLoad={activeLoad}
              setActiveLoad={setActiveLoad}
              onLoadFieldUpdate={onLoadFieldUpdate}
              maxPayInfo={maxPayInfo}
              callins={callins}
            />
            <ShotClock
              load={activeLoad}
              setAvailableToBook={setAvailableToBook}
              actionCallback={handleTableAction}
            />
            <Box sx={{ borderBottom: 1, borderColor: "divider", padding: "10px 24px 0 24px", backgroundColor: "black_solid.05" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Box sx={{ flexShrink: 0 }}>
                  <PrimaryTabs value={tabValue} handleChange={handleTabChange} tabLabels={tabLabels} />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "quick-actions", loadId: activeLoad.id })}
                    disabled={["AVA", "A-RTGDE", "ONHOLD"].includes(activeLoad.brokerageStatus)}
                    label="Call In"
                  />
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "quick-actions", tab: "1", loadId: activeLoad.id })}
                    disabled={["AVA", "A-RTGDE", "ONHOLD"].includes(activeLoad.brokerageStatus)}
                    label="Accessorial"
                  />
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "quick-actions", tab: "2", loadId: activeLoad.id })}
                    disabled={["AVA", "A-RTGDE", "ONHOLD"].includes(activeLoad.brokerageStatus)}
                    label="Comcheck"
                  />
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "quick-actions", tab: "3", loadId: activeLoad.id })}
                    disabled={["AVA", "A-RTGDE", "ONHOLD"].includes(activeLoad.brokerageStatus)}
                    label="Rate Confirmation"
                  />
                  {activeLoad.brokerageStatus != "AVA" && activeLoad.brokerageStatus != "A-RTGDE" && activeLoad.brokerageStatus != "ONHOLD" &&
                    <PrimaryButton
                      onClick={() => handleUpdateStops()}
                      label="Save"
                      disabled={stopsUpdated.length === 0}
                      isLoading={saveLoading}
                    />
                  }
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "bounce-carrier", loadId: activeLoad.id })}
                    disabled={!activeLoad.payeeId}
                    label="Bounce"
                  />
                  <PrimaryButton
                    onClick={() => handleTableAction({ action: "carrier-offer", loadId: activeLoad.id, carrierId: activeLoad.payeeId })}
                    label="Make Offer"
                  />
                  <PrimaryButton
                    onClick={() => availableToBook && handleTableAction({ action: "load-booking", loadId: activeLoad.id, carrierId: activeLoad.payeeId })}
                    disabled={!availableToBook || activeLoad.payeeId !== undefined}
                    label="Book Now"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <PrimaryTabPanel value={tabValue} index={0}>
            <LoadProfileDetailsContainer
              activeUser={activeUser}
              activeLoad={activeLoad}
              carrierExtraPay={carrierExtraPay}
              carrierExtraPayLoading={carrierExtraPayLoading}
              stopsUpdated={stopsUpdated}
              setStopsUpdated={setStopsUpdated}
              handleUpdateStops={handleUpdateStops}
              clearTimeFromStops={clearTimeFromStops}
              handleTableAction={handleTableAction}
              isLoading={clearTimeFromStopLoading}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={1}>
            <LoadFreightMatches
              isLoading={isLoading}
              activeLoad={activeLoad}
              freightMatches={freightMatches}
              setFreightMatches={setFreightMatches}
              newLoadsCount={newLoadsCount}
              radiusOptions={radiusOptions}
              radiusOrigin={radiusOrigin}
              setRadiusOrigin={setRadiusOrigin}
              radiusDest={radiusDest}
              setRadiusDest={setRadiusDest}
              handleGetMatches={handleGetMatches}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={2}>
            <LoadProfileCallins activeLoad={activeLoad} />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={3}>
            <LoadProfileLaneHistory
              activeLoad={activeLoad}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={4}>
            <LoadProfileDocuments activeLoad={activeLoad} carrierExtraPay={carrierExtraPay} />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={5}>
            <LoadProfileOffers
              activeLoad={activeLoad}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
        </Box>
      )}
      <Outlet />
    </Box>
  );
}

LoadProfile.propTypes = {
  newcomer: PropTypes.object,
  refreshPage: PropTypes.bool,
  setRefreshPage: PropTypes.func,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func.isRequired,
  handleRecentlyViewed: PropTypes.func.isRequired
};